import {FC, useEffect, useMemo, useRef, useState} from 'react';
import {toast} from 'react-toastify';
//mui
import {Box, useMediaQuery} from '@mui/material';
//store
import {useAppDispatch, useAppSelector} from '@/store/hooks';
import {clearTicketsTiers, getTicketsTiers} from '@/store/organization-tables';
import {getCurrentEventSelector, getEventsSelector} from '@/store/organization-event';
import {
  clearAttendeesData,
  getAttendeesList,
  getAttendeesStatistics,
  getAttendeeStatisticsSelector,
  // checkInAttendee, // import the actual check-in thunk
} from '@/store/organization-attendees';
//components
import StatisticUnknownGuests from '@/components/Organization/Dashboard/Statistics/StatisticsInfo/StatisticsInfoTables/StatisticUnknownGuests';
import StatisticCheckedIn from '@/components/Organization/Dashboard/Statistics/StatisticsInfo/StatisticsInfoTables/StatisticCheckedIn';
import StatisticSpecialMeals from '@/components/Organization/Dashboard/Statistics/StatisticsInfo/StatisticsInfoTables/StatisticSpecialMeals';
import AttendeesTable from '@/components/Organization/Attendees/AttendeesTable';
import TableStatistics from '@/shared/components/Table/TableStatistics';
import EmptyContent from '@/shared/components/Table/EmptyContent';
import Modal from '@/shared/components/Modal';
import Loader from '@/shared/components/Loader';
//helpers
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
//icons
import {ReactComponent as StatisticsGuestsIcon} from '@/assets/icons/statistics/statistics-guests.svg';
import {ReactComponent as StatisticsCheckedIdIcon} from '@/assets/icons/statistics/statistics-checked-in.svg';
import {ReactComponent as StatisticsMealsIcon} from '@/assets/icons/statistics/statistics-meals.svg';
import {ReactComponent as PeopleIcon} from '@/assets/icons/sidebar/people.svg';
// import {ReactComponent as StatisticsTicketIcon} from '@/assets/icons/statistics-ticket.svg';
//constants
import {CHECKED_IN, defaultPageSize, EVENT_ID, SPECIAL_MEALS, UNKNOWN_GUSTS, VIEW_DETAILS} from '@/utils/constants';
//enums
import {STATISTIC, TABLE_STATUS} from '@/utils/enums/tables.enum';
//styles
import {attendeesStyle} from './Attendees.styles';
import {ScanPassScreen} from '@/shared/components/ScanScreen';
import Button from '@/shared/components/Button';
import {OrganizationAttendeesService} from '@/utils/services';
// import {useApiInstance} from '@/contexts/ApiInstanceContext';
import {orgAPI} from '@/core/api';

const Attendees: FC = () => {
  const dispatch = useAppDispatch();

  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [activeStatisticsInfoTitle, setActiveStatisticsInfoTitle] = useState<string>('');
  const [isAttendeesInfoModalOpen, setIsAttendeesInfoModalOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);

  const events = useAppSelector(getEventsSelector);
  const currentEvent = useAppSelector(getCurrentEventSelector);
  const attendeeStatistics = useAppSelector(getAttendeeStatisticsSelector);

  const isLaptop = useMediaQuery('(min-width:1200px)');

  const isLaptopRef = useRef(isLaptop);
  useEffect(() => {
    isLaptopRef.current = isLaptop;
  }, [isLaptop]);

  useEffect(() => {
    if (!currentEvent?.id) {
      if (events && !events.length) setIsDataLoaded(true);
      return;
    }

    const fetchAllData = async () => {
      setIsDataLoaded(false);
      try {
        const reqBody = getDefaultRequestBody(currentEvent.id, EVENT_ID);

        await Promise.all([
          dispatch(getAttendeesList({...reqBody, pageSize: isLaptopRef.current ? defaultPageSize : 10000})).unwrap(),
          dispatch(getAttendeesStatistics(currentEvent.id)).unwrap(),
          ...(currentEvent.tableInfos.length
            ? [dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: orgAPI})).unwrap()]
            : [Promise.resolve(dispatch(clearTicketsTiers()))]),
          // dispatch(getTicketsTiers({reqBody: currentEvent.id, apiInstance: orgAPI})).unwrap(),
        ]);
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setIsDataLoaded(true);
      }
    };

    fetchAllData();
    // Fix: removed isLaptop from deps — resize should not re-fetch all data
  }, [currentEvent?.id, events?.length, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearAttendeesData());
    };
  }, [dispatch]);

  const unknownGuestsStatisticsTitle = useMemo(
    () => `${attendeeStatistics?.unknownGuests} of ${attendeeStatistics?.allGuests}`,
    [attendeeStatistics]
  );

  const checkedInStatisticsTitle = useMemo(
    () => `${attendeeStatistics?.checkedIn} of ${attendeeStatistics?.allGuests}`,
    [attendeeStatistics]
  );

  const handleCloseAttendeesInfoModal = () => setIsAttendeesInfoModalOpen(false);

  const activeStatisticsTable = useMemo(() => {
    switch (activeStatisticsInfoTitle) {
      case STATISTIC.UNKNOWN_GUESTS:
        return <StatisticUnknownGuests />;
      case STATISTIC.CHECKED_IN:
        return <StatisticCheckedIn />;
      case STATISTIC.SPECIAL_MEALS:
        return <StatisticSpecialMeals />;
      default:
        return null;
    }
  }, [activeStatisticsInfoTitle]);

  const handleStatisticsClick = (title: string) => {
    setActiveStatisticsInfoTitle(title);
    setIsAttendeesInfoModalOpen(true);
  };

  const handleCheckInComplete = async (guestId: string) => {
    if (!currentEvent?.id) return;
    try {
      const reqBody = getDefaultRequestBody(currentEvent.id, EVENT_ID);

      await OrganizationAttendeesService.updateAttendeesStatus(+guestId, TABLE_STATUS.CHECKED_IN);
      await dispatch(getAttendeesList({...reqBody, pageSize: isLaptopRef.current ? defaultPageSize : 10000})).unwrap();
      await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();
      toast.success('Guest checked in successfully');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  return (
    <Box sx={attendeesStyle.wrapper}>
      {isDataLoaded ? (
        !!events?.length && attendeeStatistics ? (
          <Box>
            <Box sx={attendeesStyle.content}>
              <TableStatistics
                icon={StatisticsGuestsIcon}
                title={unknownGuestsStatisticsTitle}
                description={UNKNOWN_GUSTS}
                onClick={handleStatisticsClick}
                actionsData={[VIEW_DETAILS]}
              />
              <TableStatistics
                icon={StatisticsCheckedIdIcon}
                title={checkedInStatisticsTitle}
                description={CHECKED_IN}
                onClick={handleStatisticsClick}
                actionsData={[VIEW_DETAILS]}
              />
              <TableStatistics
                icon={StatisticsMealsIcon}
                title={attendeeStatistics.specialMeals}
                description={SPECIAL_MEALS}
                onClick={handleStatisticsClick}
                actionsData={[VIEW_DETAILS]}
              />
              {isLaptop && <Button title="Scan QR Code" onClick={() => setIsScannerOpen(true)} sx={{height: '76px'}} />}
            </Box>

            <AttendeesTable />

            <Modal
              title={activeStatisticsInfoTitle}
              onClose={handleCloseAttendeesInfoModal}
              open={isAttendeesInfoModalOpen}
              sx={attendeesStyle.modal}
            >
              {activeStatisticsTable}
            </Modal>
          </Box>
        ) : (
          <EmptyContent title="You do not have any attendees." icon={PeopleIcon} />
        )
      ) : (
        <Loader parentClassName={attendeesStyle.loader} size={50} />
      )}

      <Box sx={{height: '100vh'}}>
        <ScanPassScreen
          open={isScannerOpen}
          onClose={() => setIsScannerOpen(false)}
          onCheckInComplete={handleCheckInComplete}
        />
      </Box>
    </Box>
  );
};

export default Attendees;
